import { useSyncExternalStore } from 'react';
import { currentHref, subscribeHref } from '../router/router';

/**
 * The two facts this tab keeps about the other ones. Both are module state rather than React state,
 * and each for its own reason.
 *
 * - **The import's flow.** The control that starts it is in the header, which is rendered *outside*
 *   `PlaybackHost`, while the dialog and the act itself are inside it — what is playing is half of
 *   the list the import builds. There is no context both sides share. And the flow has to survive a
 *   re-mount: taking the videos in changes what `PlaybackHost` returns, which re-mounts everything
 *   under it, so a `useState` holding "the collection is out" would be thrown away in the middle of
 *   the collection.
 * - **The handover.** A tab that gave its videos away and could not close has to stop playing, drop
 *   its list and say so, and the three places that implement that are a component, a host and a
 *   store's cleanup. One module-level fact rather than a prop threaded through all three.
 *
 * Neither survives a reload, and neither should: both describe something happening now.
 */

/* ------------------------------------------------------------------------------------------------
 * The import's flow
 * ---------------------------------------------------------------------------------------------- */

/**
 * - `idle` — nothing on screen.
 * - `probing` — the press has gone out and this tab is waiting to hear whether any other one has
 *   videos. The control itself is what says so: it turns into a spinner and stops taking presses
 *   until the answer decides which of the next two states it is.
 * - `asking` — a tab answered, so the confirmation is up.
 * - `working` — the collection is out, or the tabs that answered are being told to close.
 * - `stayed` — at least one tab handed its videos over and the browser would not let it close.
 *   The only outcome with a sentence: everything closing is silent.
 * - `empty` — no other tab has videos. The header says so under the control, since there is nothing
 *   to confirm and nothing a dialog could offer.
 */
export type TabImportFlow = 'idle' | 'probing' | 'asking' | 'working' | 'stayed' | 'empty';

let flow: TabImportFlow = 'idle';

export function setImportFlow(next: TabImportFlow): void {
  if (flow === next) return;
  flow = next;
  notify();
}

/** Any press or click takes the line back, which is the whole of what it asks of the reader. */
export function clearEmptyNotice(): void {
  if (flow !== 'empty') return;
  setImportFlow('idle');
}

/**
 * Read outside React by the steps themselves (`tabs/useTabImport.ts`): each one runs on a timer and
 * has to check that it is still the one in hand before it writes anything.
 */
export function importFlow(): TabImportFlow {
  return flow;
}

export function useImportFlow(): TabImportFlow {
  return useSyncExternalStore(subscribe, () => flow, IDLE);
}

/* ------------------------------------------------------------------------------------------------
 * The handover
 * ---------------------------------------------------------------------------------------------- */

/**
 * Where this tab was standing when it handed its videos over, and `null` for a tab that has not.
 *
 * The **href** rather than a boolean, and that is what makes the state end by itself. A tab the
 * browser refused to close is still a working tab: the reader closes it by hand, or they navigate
 * somewhere and carry on using it. Navigating away is therefore the end of the handover — there is
 * nothing left to say about a page they are no longer on — and comparing hrefs says so without a
 * reset that something has to remember to call. A boolean cleared by an effect on mount would be
 * worse than untidy: the component that would own the effect is re-mounted by the very render the
 * handover causes, so it would clear the flag it had just set.
 */
let handedOverAt: string | null = null;

/** This tab gave its videos to another one, and the browser would not let it close. */
export function markHandedOver(): void {
  handedOverAt = currentHref();
  notify();
}

/**
 * Read outside React by the resume-position writer, which has to know at the instant its cleanup
 * runs — see `history/useWatchProgress.ts`.
 */
export function hasHandedOver(): boolean {
  return handedOverAt !== null && handedOverAt === currentHref();
}

export function useHandedOver(): boolean {
  return useSyncExternalStore(subscribe, hasHandedOver, FALSE);
}

/* ------------------------------------------------------------------------------------------------
 * The subscription both share
 * ---------------------------------------------------------------------------------------------- */

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * One navigation listener for the whole document, installed on the first subscriber and never taken
 * down — the rule `platform/settings.ts` and the four stores beside it keep, for the reason
 * docs/specs/web-ui.md records: `useSyncExternalStore` calls `subscribe` once per *mounted
 * component*, so a listener registered in here is a listener per subscriber.
 *
 * It is the navigation rather than a write that has to be heard: {@link hasHandedOver} reads the
 * address bar, so its answer changes when the reader moves and nothing in this file was called.
 */
let watchingHref = false;

function subscribe(listener: () => void): () => void {
  if (!watchingHref) {
    watchingHref = true;
    subscribeHref(notify);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable, so a server snapshot cannot re-render forever. */
const FALSE = (): boolean => false;
const IDLE = (): TabImportFlow => 'idle';
